<template>
  <div class="downloads-page">
    <div
      v-if="!isLoaded"
      class="text-center p-5"
    >
      <b-spinner />
    </div>
    <template v-else>
      <div v-if="!downloads.length">
        <div class="items-empty-message-container">
          <div class="mb-1">
            <send-icon />
          </div>
          <b-card-text class="list-empty-text-color">
            There are no downloads yet.
          </b-card-text>
        </div>
      </div>
      <div v-else>
        <b-card
          no-body
          class="mb-0"
        >
          <div class="dashboard-title p-2">
            Downloads
          </div>
          <b-table
            v-if="isLoaded"
            ref="refshipmentListTable"
            class="position-relative"
            :per-page="queryParams.limit"
            :items="downloads"
            responsive
            :fields="tableColumns"
            primary-key="_id"
            show-empty
          >
            <template #cell(createdAt)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">{{
                  getDateFromTimestamp(data.item.createdAt)
                }} {{ getFromattedTimeFromDate(data.item.createdAt) }}
                </span>
              </div>
            </template>
            <template #cell(downloadType)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">{{
                  getDownloadTypeName(data.item.downloadType)
                }}</span>
              </div>
            </template>
            <template #cell(status)="data">
              <b-badge
                pill
                :class="getOrderStatusCssClass(data.item.status)"
                class="text-capitalize table-status color-black"
              >
                {{ getDownloadStatus(data.item.status) }}
              </b-badge>
              <small
                v-if="getErrorReason(data.item)"
                class="download-error-reason d-block mt-25 text-muted text-wrap text-break"
              >
                {{ getErrorReason(data.item) }}
              </small>
            </template>
            <template #cell(downloadUrl)="data">
              <div
                v-if="data.item.downloadUrl"
                class="d-flex color-blue align-items-center"
              >
                <div
                  class="download-link mr-50 cursor-pointer"
                  @click="download(data.item.downloadUrl)"
                >
                  Click here
                </div>
                <feather-icon
                  icon="CopyIcon"
                  size="18"
                  class="cursor-pointer"
                  @click="copyDownloadLink(data.item.downloadUrl)"
                />
              </div>
            </template>
          </b-table>
        </b-card>
        <div
          v-if="hasPagination"
          class="my-2"
        >
          <b-row>
            <!-- Pagination -->
            <b-col
              cols="12"
              class="d-flex align-items-center justify-content-center"
            >
              <b-pagination
                v-model="queryParams.page"
                :total-rows="totaldownloads"
                :per-page="queryParams.limit"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0 page-link-class"
                prev-class="prev-item"
                next-class="next-item"
                @input="getDownloadsList"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { reactive } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BPagination,
  BSpinner,
  BCard,
  BTable,
  BBadge,
  BCardText,
} from 'bootstrap-vue'
import SendIcon from '@/@core/assets/svg-components/SendIcon.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { useDownloads } from '@/composables/useDownloads'

export default {
  components: {
    BRow,
    BCol,
    BPagination,
    BSpinner,
    BCard,
    BTable,
    SendIcon,
    FeatherIcon,
    BBadge,
    BCardText,
  },
  setup() {
    const dl = useDownloads()
    dl.initDownloads()

    // Bridge: template uses queryParams.page (v-model) and queryParams.limit
    const queryParams = reactive({ page: 1, limit: dl.perPage.value })

    // Wrap getDownloadsList to sync queryParams.page → composable currentPage
    const getDownloadsList = () => {
      dl.currentPage.value = queryParams.page
      dl.getDownloadsList()
    }

    return {
      ...dl,
      queryParams,
      // Template uses "totaldownloads" (lowercase d)
      totaldownloads: dl.totalDownloads,
      getDownloadsList,
    }
  },
  methods: {
    getOrderStatusCssClass(status) {
      if (status === this.DOWNLOAD_FILE_STATUS.ERROR.VALUE) return 'rejected'
      if (status === this.DOWNLOAD_FILE_STATUS.READY_TO_DOWNLOAD.VALUE) return 'confirmed'
      return 'pending'
    },
  },
}
</script>

<style lang="scss" >
.dashboard-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

// This block is not scoped, so the class is kept under the class of this page.
.downloads-page .download-error-reason {
  max-width: 240px;
}
</style>
